import { AutoAwesome, ContentCopy, DragIndicator } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { CellAiConfigView, FormField } from "generated/graphql";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { useState } from "react";
import { Draggable, DraggableProvided, DraggableStateSnapshot, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router";
import theme, { EllipsisSX } from "theme";
import { P } from "theme/typography";
import {
  fetchCellAiConfigs,
  selectLensEditorFieldAiConfigs,
  selectLensEditorFieldTagConfigs,
} from "redux/slices/lensEditor";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useSnackbar } from "notistack";
import StreamIcon from "@mui/icons-material/Stream";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";

interface SectionFieldListProps {
  sectionId: number;
  fields: FormField[];
}

interface FieldRowProps {
  field: FormField;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
}

const FieldRow = ({ field, provided, snapshot }: FieldRowProps) => {
  const [hover, setHover] = useState(false);
  const { fieldId, lensId } = useParamNumbers(["fieldId", "lensId"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fieldAiConfigs = useAppSelector(selectLensEditorFieldAiConfigs);
  const fieldTagConfigs = useAppSelector(selectLensEditorFieldTagConfigs);
  const aiConfig = fieldAiConfigs.find((f) => f.fieldId === field.id);
  const copyFieldConfigToClipboard = (field: FormField) => {
    const fieldConfig = (field: FormField, fieldCellAiConfigs: CellAiConfigView[]) => {
      return {
        field: field,
        fieldAIConfig: fieldAiConfigs.find((f) => f.fieldId === field.id),
        cellAIConfigs: fieldCellAiConfigs,
        fieldTagConfig: fieldTagConfigs.find((f) => f.fieldId === field.id),
      };
    };
    if (lensId === undefined) return;
    dispatch(
      fetchCellAiConfigs({
        fieldId: field.id,
        lensId,
      })
    )
      .unwrap()
      .then((result) => {
        const fieldCellAiConfigs = result.lens.formQueries.cellAiConfigs;
        const json = JSON.stringify(fieldConfig(field, fieldCellAiConfigs), null, 2);
        try {
          void navigator.clipboard.writeText(json);
          enqueueSnackbar("Field JSON copied to clipboard", { variant: "success" });
        } catch (e) {
          enqueueSnackbar("Unable to copy field JSON to clipboard", { variant: "error" });
        }
      });
  };

  return (
    <div
      style={{ display: "flex" }}
      ref={provided.innerRef}
      {...provided.draggableProps}
      onClick={() => {
        const isOnSettings = window.location.pathname.includes("settings");
        navigate("sections/" + field.sectionId + "/fields/" + field.id + (isOnSettings ? "/settings" : "/cells"), {});
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <Box
        sx={{
          height: "44px",
          backgroundColor: snapshot.isDragging
            ? theme.palette.primary.light
            : field.id === fieldId
              ? theme.palette.grey[50]
              : hover
                ? theme.palette.grey[50]
                : "white",
          borderRadius: 0.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          cursor: "pointer",
          maxWidth: "100%",
          px: 1,
        }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
          }}>
          {aiConfig && (
            <Tooltip title="Ai Config">
              <AutoAwesome color="primary" sx={{ mr: 1 }} />
            </Tooltip>
          )}
          {aiConfig?.live && (
            <Tooltip title="Field is Live">
              <StreamIcon color="primary" sx={{ mr: 1 }} />
            </Tooltip>
          )}
          <Tooltip title={field.prompt}>
            <P variant="xsBold" sx={EllipsisSX}>
              {field.outlinePrefix} {field.name}
              {field.required ? "*" : ""}
            </P>
          </Tooltip>
          <DescriptionTooltip description={field.description} sx={{ ml: 1 }} />
          <P
            variant="xs"
            sx={{
              ml: 1,
              whiteSpace: "nowrap",
            }}>
            {field.cells.length > 0 ? field.cells.length + (field.cells.length === 1 ? " cell" : " cells") : ""}
          </P>
          {/*Spacer for ellipses*/}
          <Box
            sx={{
              flexGrow: 1,
              px: 1,
            }}></Box>
          <Box
            sx={{
              display: hover ? "flex" : "none",
              alignItems: "center",
              p: 0,
              m: 0,
              gap: 2,
            }}>
            <Tooltip title="Copy Field JSON">
              <IconButton
                onClick={(evt) => {
                  evt.stopPropagation();
                  copyFieldConfigToClipboard(field);
                }}
                sx={{
                  p: 0,
                  m: 0,
                }}>
                <ContentCopy
                  sx={{
                    width: "1rem",
                    height: "1rem",
                  }}
                />
              </IconButton>
            </Tooltip>
            <span {...provided.dragHandleProps}>
              <IconButton
                sx={{
                  p: 0,
                  m: 0,
                }}>
                <DragIndicator />
              </IconButton>
            </span>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export const SectionFieldsList = (props: SectionFieldListProps) => {
  return (
    <Droppable droppableId={props.sectionId.toString()} type="field">
      {(provided) => (
        <div
          ref={provided.innerRef}
          style={{
            minHeight: "16px",
          }}>
          {props.fields.map((item) => {
            return (
              <Draggable key={item.id} draggableId={item.id.toString()} index={item.rank}>
                {(provided, snapshot) => <FieldRow field={item} provided={provided} snapshot={snapshot} />}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};
